import { useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { baseAPI } from '~shared/api/base-api';
import { StudioFormCard } from '~features/form-settings';
import type { StudioFormValues } from '~features/form-settings';
import { useGetPublicFormConfig } from '~features/form-settings/api/get-public-form-config';
import { useEmbedAutoResize } from '~features/form-settings/lib/embed-resize';

const searchSchema = z.object({
  studioId: z.string().catch(''),
  formType: z.string().catch(''),
  formId: z.string().optional().catch(undefined),
  utm_source: z.string().optional().catch(undefined),
  utm_medium: z.string().optional().catch(undefined),
  utm_campaign: z.string().optional().catch(undefined),
  utm_content: z.string().optional().catch(undefined),
  utm_term: z.string().optional().catch(undefined),
  gclid: z.string().optional().catch(undefined),
  fbclid: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/embed/form')({
  validateSearch: (search) => searchSchema.parse(search),
  component: EmbedFormPage,
});

function EmbedFormPage() {
  const search = Route.useSearch();
  const { studioId, formType, formId } = search;
  const {
    data: config,
    isLoading,
    isError,
  } = useGetPublicFormConfig(studioId, formType, formId);

  if (!formId && (!studioId || !formType)) {
    return <EmbedShell>Invalid form link.</EmbedShell>;
  }
  if (isLoading) {
    return (
      <EmbedShell>
        <Loader2 className='size-6 animate-spin text-muted-foreground' />
      </EmbedShell>
    );
  }
  if (isError || !config) {
    return <EmbedShell>This form is not available right now.</EmbedShell>;
  }

  const handleSubmit = async (values: StudioFormValues) => {
    await baseAPI.post('forms/submit', {
      studioId,
      formType,
      formId: formId ?? undefined,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      childFirstName: values.childFirstName || undefined,
      childAge: values.childAge || undefined,
      sessionInterest: values.sessionInterest || undefined,
      consent: values.consent,
      website: values.website,
      referrer: document.referrer || undefined,
      sourcePageUrl: document.referrer || undefined,
      utmSource: search.utm_source,
      utmMedium: search.utm_medium,
      utmCampaign: search.utm_campaign,
      utmContent: search.utm_content,
      utmTerm: search.utm_term,
      gclid: search.gclid,
      fbclid: search.fbclid,
      timestamp: new Date().toISOString(),
    });
    if (config.redirectUrl) {
      const target = window.top ?? window;
      target.location.href = config.redirectUrl;
    }
  };

  return (
    <EmbedShell>
      <StudioFormCard
        theme={config.theme ?? {}}
        fieldVisibility={config.fieldVisibility ?? {}}
        interestOptions={config.interestOptions}
        studioName={config.studioName ?? undefined}
        onSubmit={handleSubmit}
      />
    </EmbedShell>
  );
}

function EmbedShell({ children }: { children: React.ReactNode }) {
  // No min-h-screen: the wrapper must shrink-wrap its content so the parent
  // page can size the iframe to the real card height (ENR-1474).
  const shellRef = useRef<HTMLDivElement>(null);
  useEmbedAutoResize(shellRef);
  return (
    <div
      ref={shellRef}
      className='flex items-center justify-center bg-transparent p-4 text-sm text-gray-600'
    >
      {children}
    </div>
  );
}
